export const V2_THEME = "neutral-light";
export const V2_SKINS = [
  { id: "warm-light", label: "Warm Light" },
  { id: "neutral-light", label: "Neutral Light" },
  { id: "dark-pro", label: "Dark Professional" },
];

export function v2cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

/**
 * Centralized visual tokens for v2.
 * Keep component styles composed from these primitives to make theme iteration fast.
 */
const warmLightTokens = {
  app: {
    root: "h-full max-h-full overflow-hidden bg-stone-50 text-slate-900",
    shell: "flex h-full max-h-full overflow-hidden flex-col lg:flex-row",
    content: "flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden",
    header: "shrink-0 border-b border-stone-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80",
    headerSubtitle: "hidden md:block text-xs text-slate-500 mt-1",
    headerBar: "px-4 py-3 md:px-6 md:py-4 flex items-center justify-between gap-3",
    mainWrap:
      "flex-1 min-h-0 overflow-y-auto px-4 py-4 md:px-6 md:py-8 overscroll-y-contain",
    mainFlush: "-mx-4 -my-4 md:-mx-6 md:-my-8",
    mainMax: "max-w-6xl w-full h-full min-h-0 flex flex-col",
  },
  sidebar: {
    root: "hidden lg:flex w-20 shrink-0 border-r border-stone-200 bg-white",
    logoWrap: "w-10 h-10 rounded-lg bg-stone-100 border border-stone-200 flex items-center justify-center",
    itemBase:
      "w-full rounded-xl py-2.5 flex flex-col items-center gap-1 border transition-colors",
    itemIdle: "bg-transparent border-transparent hover:bg-stone-100 hover:border-stone-200",
    itemActive: "bg-cyan-50 border-cyan-300",
    iconIdle: "text-slate-500",
    iconActive: "text-cyan-700",
    labelIdle: "text-[10px] leading-none text-slate-500",
    labelActive: "text-[10px] leading-none text-cyan-700",
  },
  mobileNav: {
    root: "lg:hidden shrink-0 border-t border-stone-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90 pb-[env(safe-area-inset-bottom,0px)]",
    inner: "grid grid-cols-5 gap-0.5 px-1 pt-1 pb-1",
    item: "relative flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 transition-colors touch-manipulation",
    itemIdle: "text-slate-500 active:bg-stone-100",
    itemActive: "text-cyan-700 bg-cyan-50",
    labelIdle: "text-[10px] font-medium leading-none text-slate-500",
    labelActive: "text-[10px] font-semibold leading-none text-cyan-700",
    sheet:
      "absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl border border-stone-200 bg-white px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl",
    sheetItem:
      "flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm text-slate-800 active:bg-stone-100",
    sheetItemActive: "bg-cyan-50 text-cyan-800",
  },
  surface: {
    card: "rounded-xl border border-stone-200 bg-white shadow-sm",
    cardSoft: "rounded-xl border border-stone-200 bg-white",
    panelHeader: "px-4 py-3 border-b border-stone-200",
    mutedText: "text-slate-600",
    subtleText: "text-slate-500",
  },
  button: {
    primary: "px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-white text-sm",
    success: "px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm",
    warning: "px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm",
    danger: "px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm",
    secondary:
      "px-3 py-1.5 rounded-lg border border-stone-300 text-slate-700 hover:bg-stone-100 text-sm",
    chip: "px-2.5 py-1 rounded-full border text-xs",
    chipIdle: "border-stone-300 text-slate-600 hover:bg-stone-100",
    chipActive: "border-cyan-300 bg-cyan-50 text-cyan-700",
  },
  status: {
    completed: "border-emerald-300 text-emerald-700 bg-emerald-50",
    failed: "border-rose-300 text-rose-700 bg-rose-50",
    running: "border-cyan-300 text-cyan-700 bg-cyan-50",
    pending: "border-amber-300 text-amber-700 bg-amber-50",
  },
};

const neutralLightTokens = {
  app: {
    root: "h-full max-h-full overflow-hidden bg-slate-50 text-slate-900",
    shell: "flex h-full max-h-full overflow-hidden flex-col lg:flex-row",
    content: "flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden",
    header: "shrink-0 border-b border-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/80",
    headerSubtitle: "hidden md:block text-xs text-slate-500 mt-1",
    headerBar: "px-4 py-3 md:px-6 md:py-4 flex items-center justify-between gap-3",
    mainWrap:
      "flex-1 min-h-0 overflow-y-auto px-4 py-4 md:px-6 md:py-8 overscroll-y-contain",
    mainFlush: "-mx-4 -my-4 md:-mx-6 md:-my-8",
    mainMax: "max-w-6xl w-full h-full min-h-0 flex flex-col",
  },
  sidebar: {
    root: "hidden lg:flex w-20 shrink-0 border-r border-slate-200 bg-white",
    logoWrap: "w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center",
    itemBase:
      "w-full rounded-xl py-2.5 flex flex-col items-center gap-1 border transition-colors",
    itemIdle: "bg-transparent border-transparent hover:bg-slate-100 hover:border-slate-200",
    itemActive: "bg-blue-50 border-blue-300",
    iconIdle: "text-slate-500",
    iconActive: "text-blue-700",
    labelIdle: "text-[10px] leading-none text-slate-500",
    labelActive: "text-[10px] leading-none text-blue-700",
  },
  mobileNav: {
    root: "lg:hidden shrink-0 border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90 pb-[env(safe-area-inset-bottom,0px)]",
    inner: "grid grid-cols-5 gap-0.5 px-1 pt-1 pb-1",
    item: "relative flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 transition-colors touch-manipulation",
    itemIdle: "text-slate-500 active:bg-slate-100",
    itemActive: "text-blue-700 bg-blue-50",
    labelIdle: "text-[10px] font-medium leading-none text-slate-500",
    labelActive: "text-[10px] font-semibold leading-none text-blue-700",
    sheet:
      "absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl border border-slate-200 bg-white px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl",
    sheetItem:
      "flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm text-slate-800 active:bg-slate-100",
    sheetItemActive: "bg-blue-50 text-blue-800",
  },
  surface: {
    card: "rounded-xl border border-slate-200 bg-white shadow-sm",
    cardSoft: "rounded-xl border border-slate-200 bg-white",
    panelHeader: "px-4 py-3 border-b border-slate-200",
    mutedText: "text-slate-600",
    subtleText: "text-slate-500",
  },
  button: {
    primary: "px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-600 text-white text-sm",
    success: "px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm",
    warning: "px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm",
    danger: "px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm",
    secondary:
      "px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm",
    chip: "px-2.5 py-1 rounded-full border text-xs",
    chipIdle: "border-slate-300 text-slate-600 hover:bg-slate-100",
    chipActive: "border-blue-300 bg-blue-50 text-blue-700",
  },
  status: {
    completed: "border-emerald-300 text-emerald-700 bg-emerald-50",
    failed: "border-rose-300 text-rose-700 bg-rose-50",
    running: "border-blue-300 text-blue-700 bg-blue-50",
    pending: "border-amber-300 text-amber-700 bg-amber-50",
  },
};

const darkProTokens = {
  app: {
    root: "h-full max-h-full overflow-hidden bg-slate-950 text-slate-100",
    shell: "flex h-full max-h-full overflow-hidden flex-col lg:flex-row",
    content: "flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden",
    header: "shrink-0 border-b border-slate-800 bg-slate-900/90 backdrop-blur supports-[backdrop-filter]:bg-slate-900/80",
    headerSubtitle: "hidden md:block text-xs text-slate-400 mt-1",
    headerBar: "px-4 py-3 md:px-6 md:py-4 flex items-center justify-between gap-3",
    mainWrap:
      "flex-1 min-h-0 overflow-y-auto px-4 py-4 md:px-6 md:py-8 overscroll-y-contain",
    mainFlush: "-mx-4 -my-4 md:-mx-6 md:-my-8",
    mainMax: "max-w-6xl w-full h-full min-h-0 flex flex-col",
  },
  sidebar: {
    root: "hidden lg:flex w-20 shrink-0 border-r border-slate-800 bg-slate-950",
    logoWrap: "w-10 h-10 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center",
    itemBase:
      "w-full rounded-xl py-2.5 flex flex-col items-center gap-1 border transition-colors",
    itemIdle: "bg-transparent border-transparent hover:bg-slate-900 hover:border-slate-800",
    itemActive: "bg-cyan-900/40 border-cyan-700/60",
    iconIdle: "text-slate-400",
    iconActive: "text-cyan-300",
    labelIdle: "text-[10px] leading-none text-slate-500",
    labelActive: "text-[10px] leading-none text-cyan-200",
  },
  mobileNav: {
    root: "lg:hidden shrink-0 border-t border-slate-800 bg-slate-950/95 backdrop-blur supports-[backdrop-filter]:bg-slate-950/90 pb-[env(safe-area-inset-bottom,0px)]",
    inner: "grid grid-cols-5 gap-0.5 px-1 pt-1 pb-1",
    item: "relative flex min-h-[3.25rem] flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 transition-colors touch-manipulation",
    itemIdle: "text-slate-400 active:bg-slate-900",
    itemActive: "text-cyan-300 bg-cyan-900/40",
    labelIdle: "text-[10px] font-medium leading-none text-slate-500",
    labelActive: "text-[10px] font-semibold leading-none text-cyan-200",
    sheet:
      "absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl border border-slate-800 bg-slate-900 px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl",
    sheetItem:
      "flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-sm text-slate-100 active:bg-slate-800",
    sheetItemActive: "bg-cyan-900/40 text-cyan-100",
  },
  surface: {
    card: "rounded-xl border border-slate-800 bg-slate-900/70 shadow-sm",
    cardSoft: "rounded-xl border border-slate-800 bg-slate-900/70",
    panelHeader: "px-4 py-3 border-b border-slate-800",
    mutedText: "text-slate-300",
    subtleText: "text-slate-400",
  },
  button: {
    primary: "px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-white text-sm",
    success: "px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm",
    warning: "px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm",
    danger: "px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm",
    secondary:
      "px-3 py-1.5 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm",
    chip: "px-2.5 py-1 rounded-full border text-xs",
    chipIdle: "border-slate-700 text-slate-300 hover:bg-slate-800",
    chipActive: "border-cyan-700 bg-cyan-900/40 text-cyan-200",
  },
  status: {
    completed: "border-emerald-700 text-emerald-300 bg-emerald-900/20",
    failed: "border-rose-700 text-rose-300 bg-rose-900/20",
    running: "border-cyan-700 text-cyan-300 bg-cyan-900/20",
    pending: "border-amber-700 text-amber-300 bg-amber-900/20",
  },
};

export const v2Themes = {
  "warm-light": warmLightTokens,
  "neutral-light": neutralLightTokens,
  "dark-pro": darkProTokens,
};

export function getV2Tokens(skin) {
  return v2Themes[skin] || v2Themes[V2_THEME];
}

export const v2Tokens = getV2Tokens(V2_THEME);

export function statusTone(statusRaw) {
  return statusToneFor(statusRaw, v2Tokens);
}

export function statusToneFor(statusRaw, tokens) {
  const s = String(statusRaw || "").toLowerCase();
  const t = tokens || v2Tokens;
  if (s === "completed") return t.status.completed;
  if (s === "failed" || s === "cancelled") return t.status.failed;
  if (s === "running" || s === "processing") return t.status.running;
  return t.status.pending;
}
